<template>
  <div class="modal fade" :id="id" data-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog modal-lg" role="document">
      <form @submit.prevent="submitForm" class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ edit ? $t('feed.update_publish') : $t('feed.create_publish') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
                <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                          fill="currentColor" />
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                          fill="currentColor" />
                  </svg>
                </span>
            </div>
          </div>

          <div class="modal-body">
            <div class="d-flex align-items-center mb-3">
              <div class="me-2">
                <ManyAvatar :size="1" :data="getUser"/>
              </div>
              <h2>{{props.user?.name || currentUser?.fullname}}</h2>
            </div>

            <Input
              container-class="col-12 form-group mb-5"
              :name="`report_title_`+id"
              type="text"
              :label="$t('projects.reports.title')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-lg form-control-solid'"
              size="lg"
              :isRequired="true" 
              v-model="formData.title"
            />

            <div class="d-flex justify-content-between align-items-center mb-5">
              <div>{{ $t("feed.whats_up") }} {{props.user?.name || currentUser?.fullname}}</div>
              <div>
                <CheckboxInput
                  :label="$t('feed.customer_can_see')"
                  :is-required="false"
                  v-model="formData.is_private"
                  :bottom="false"/>
              </div>
            </div>

            <CKEditor
              :id="`publish_content_`+id"
              :content="formData.content"
              :isRequired="false"
              @text="setDescription"
            />

            <div v-if="action == 'FILE'" class="mt-5">
              <h2 class="mb-3">{{ $t('feed.image_video') }}</h2>
              <fileComponent :form="formData"/>
            </div>

            <div v-if="action == 'identifier' || formData.mention_ids?.length > 0" class="mt-5">
              <h2 class="mb-3">{{ $t('feed.identifier') }}</h2>
              <Select2
                  containerClass="col-md-12 mb-5"
                  :name="'users_'+id"
                  :multiple="true"
                  :selectClass="'form-select form-select-solid selectUserGroup'"
                  :isRequired="true"
                  :placeholderText="$t('group.users_placeholder')"
                  :parentId="'#' + id"
                  v-model="formData.mention_ids">
                  <option v-for="user in users" :key="user.id" :value="user.id">{{ user?.lastname }}  {{ user?.firstname }}</option>                
              </Select2>
            </div>

            <div v-if="action == 'LOCATE'" class="mt-5">
              <h2 class="mb-3">{{ $t('feed.locate') }}</h2>
              <locateComponent @setLocate="getLocate" :latitude="formData.latitude" :longitude="formData.longitude"/>
            </div>

            <div class="d-flex justify-content-center px-10 mt-10">
              <div class="badge_action me-10" :class="action == 'FILE' ? 'active' : ''" @click="setAction('FILE')">
                <i class="fas fa-image image me-2" :class="action == 'FILE' ? 'active' : ''"></i>{{ $t('feed.image_video') }}
              </div>
              <div class="badge_action me-10" :class="action == 'identifier' || formData.mention_ids?.length > 0 ? 'active' : ''" @click="setAction('identifier')">
                <i class="fas fa-users image me-2" :class="action == 'identifier' || formData.mention_ids?.length > 0 ? 'active' : ''"></i>{{ $t('feed.identifier') }}
              </div>
              <div class="badge_action me-10" :class="action == 'LOCATE' ? 'active' : ''" @click="setAction('LOCATE')">
                <i class="fa fa-map-marker image me-2" :class="action == 'LOCATE' ? 'active' : ''"></i>{{ $t('feed.localisation') }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel" @click="closeModal"></Button>
            <Button btn-class="btn-sm me-3" type="submit" id="submit_btn" btn-text="feed.publish" size="7px 30px" :is-loading="isLoadingFeed"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>

</template>

<script setup>
import {ref, defineProps, computed} from 'vue'
import ManyAvatar from "@/components/ManyAvatar";
import CheckboxInput from '@/components/CheckboxInput.vue';
import CKEditor from "@/components/CKEditor.vue";
import fileComponent from './fileComponent.vue';
import locateComponent from '@/components/MapComponent';
import Button from "@/components/Button";
import Input from "@/components/Input.vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";
import Select2 from "@/components/Select2.vue";

const emits = defineEmits(["load"]);

const props = defineProps({
    id: {
      type: String,
      required: true
    },
    form: {
      type: Object,
      default: {
        title: '',
        content: '',
        is_private: false,
        story_group_id: '',
        files: [],
        attach_files: [],
        mention_ids: [],
        model: "project",
        model_id: "",
        latitude: 0,
        longitude: 0,
        location_name: '',
        address: ''
      }
    },
    edit: {
      type: Boolean,
      default: false
    },
    user: Object
})

const store = useStore()
const route = useRoute()
const isLoadingFeed  = ref(false)
const formData = ref(props.form)
const action = ref('')

const currentUser = computed(() => store.getters['user/currentUser'])
const getUser = computed(() => {
    let fullname = props.user?.name?.split(' ') || currentUser.value?.fullname?.split(' ')
    return fullname ? [{firstname: fullname[0], lastname: fullname[1]}] : []
})
const users = computed(() => store.getters["user/getUsers"])


function closeModal(){
  $('#'+props.id).modal('toggle')
}

function setAction(text) {
  action.value = (action.value == text) ? '' : text
}

function setDescription(text) {
  formData.value.content = text
}

function submitForm() {
  isLoadingFeed.value = true;

  formData.value.model_id = route.params.id
  formData.value.model = 'project'
  let action = props.edit ? 'feeds/updateFeed' : 'feeds/createFeed';
  let message = props.edit ? 'feed.updated_successfully' : 'feed.created_successfully';
  
  store
    .dispatch(action, formData.value)
    .then(() => {
      isLoadingFeed.value = false;
      emits("load");
      closeModal();

      window.toastr["success"](i18n.global.t(message));
    })
    .catch((e) => {
      isLoadingFeed.value = false;
    });
}

function getLocate(locate) {
  formData.value.latitude = locate.latitude
  formData.value.longitude = locate.longitude
  formData.value.location_name = locate.location
  formData.value.address = locate.address
}
</script>

<style scoped>
  .badge_action {
    background-color: var(--bs-gray-100);
    color: var(--bs-primary);
    border-radius: 10px;
    padding: 5px 10px;
  }

  .badge_action:hover, .active {
    background-color: var(--bs-primary);
    color: #fff;
    border-radius: 10px;
    padding: 5px 10px;
  }

  .image {
    color: var(--bs-primary);
  }

  .badge_action:hover .image, .active {
    color: #fff;
  }
</style>